import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetReportTypesQuery,
  useGetReportGroupsQuery,
  useGetReportProjectUnitsQuery,
  type IReportAhuRow,
  type IReportType,
} from 'app/services/reportsBoardApi';
import { useStylesheets } from 'hooks/useStylesheets';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { usePopupKeys } from 'hooks/usePopupKeys';
import { getBootUserId } from 'hooks/useDashboardData';
import GlobalHeader from 'pages/dashboard/components/GlobalHeader';
import {
  computeCanGenerate,
  computeConditionalUI,
  computeEmptyBoardMessage,
  filterAhusForCurrentReport,
  getReportType,
  groupAhusByMain,
  isAhuReport,
  navigateHash,
  parseHash,
  sectionFlagKey,
  type HashState,
} from './reportScopes';
import { generateReport, type GenerateState, type GenerateUi } from './generateReport';
import { hasGeneratedReport, reportCacheKey } from './reportTabData';
import ReportsMenu from './components/ReportsMenu';
import ReportsHeader from './components/ReportsHeader';
import { coilAnalyticsIsoDate } from './components/CoilAnalyticsDateRange';
import EmptyBoard from './components/EmptyBoard';
import GeneratingReportTab from './components/GeneratingReportTab';
import {
  GENERATED_TAB_PANES,
  IMMEDIATE_RENDER_TABS,
  REOPENABLE_TABS,
} from './components/GeneratedReportTabs';
import ReportGenerationPopup from './components/ReportGenerationPopup';
import ReportsBoardFooter from './components/ReportsBoardFooter';

/**
 * Reports board CSS — exact legacy cascade, ONE useStylesheets call:
 * header.css → report/skeleton.css (templates/report/styles.html:2-3) →
 * common.css → report/styles.css (mvc/app.js:467-468 require.css order).
 * react.html loads common.css + header.css globally; re-injecting all four
 * here (in order, appended after the globals) reproduces the legacy
 * last-occurrence order.
 */
const REPORT_CSS = [
  '/static/css/common/header.css',
  '/static/css/report/skeleton.css',
  '/static/css/common/common.css',
  '/static/css/report/styles.css',
];

const EMPTY_AHUS: IReportAhuRow[] = [];

/**
 * Reports board (/reports/ — Backbone app `report`), Phase 5a shell + the
 * Phase 5b generation round trip.
 *
 * 5a scope: left report menu (menu.js), project + AHU selector header
 * (main_header.js), canGenerate gating, `#report*project*ahu` deep-link hash
 * routing (router.js/action.js), and the empty-board messages
 * (tabs/empty_board + action.js:26-59).
 *
 * 5b scope: the Generate button + empty-board ⟳ run the real generation kit
 * (./generateReport.ts): per-report POST dispatch, the submittal 1h guard
 * popup, the optimistic pending-report rows in the header dropdown, and the
 * legacy in-progress tab pane with its 4.5s/5s reverts.
 *
 * 5c scope: the generate-only tab panes (six 2D drawings shared, pressure
 * drop, electrical, weight, center of gravity, bill of materials, MFS profile
 * cut list) mount their 1:1 ported shells from GENERATED_TAB_PANES, filled on
 * POST success like the legacy `success → render()` (see
 * components/GeneratedReportTabs.tsx). The remaining RESULT tabs and the
 * CSV/PDF/Print flows are 5d–5e.
 *
 * Layout parity: #ksdm-content holds #left_resizer + its label + #main_menu +
 * #main_content as DIRECT SIBLINGS — the legacy collapse/expand is pure CSS
 * sibling combinators (`.left-menu-resizer:checked ~ #main_menu`,
 * report styles.css:1386-1440), so only the checkbox state lives in React
 * (plus the footer class swap, view/main.js move_footer :79-88).
 *
 * Hash is the single source of truth: menu/project/AHU selections call
 * navigateHash() (Backbone.history.navigate equivalent) and a hashchange
 * listener re-parses — so deep links, back/forward and in-page selection all
 * follow the same code path, like the legacy router round-trip.
 */
const ReportsBoardPage = () => {
  useDocumentTitle('Reports');
  useStylesheets(REPORT_CSS);
  // ESC/Enter popup keys (view/main.js:45-55 equivalent baseline).
  usePopupKeys();

  // ---- Hash state (#report*project*ahu) ----
  const [hashState, setHashState] = useState<HashState>(() =>
    parseHash(window.location.hash)
  );
  useEffect(() => {
    const onHash = () => setHashState(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const projectId = hashState.project;
  const ahuId = hashState.ahu;

  // ---- Data ----
  const { data: reports = [] } = useGetReportTypesQuery();
  const { data: groups = [] } = useGetReportGroupsQuery();
  // AHUs fetched only while a project id is present (render branch A,
  // main_header.js:162-166).
  const { data: ahusData } = useGetReportProjectUnitsQuery(
    projectId != null ? projectId : skipToken
  );
  // Stable empty reference — fresh [] per render would ripple new identities
  // through the memos/effects below.
  const ahusAll = projectId != null && ahusData ? ahusData : EMPTY_AHUS;

  // current report: the hash tag must match a fetched (role-visible) report
  // button, 'empty_board' excluded (main_header.js:106-115).
  const currentReportValue =
    hashState.report && hashState.report !== 'empty_board'
      ? reports.find((r) => r.value === hashState.report)?.value ?? null
      : null;
  const currentReport = reports.find((r) => r.value === currentReportValue) ?? null;

  // ⚠ dev-sync G / CB-2 — VERIFIED BY INSPECTION ONLY, NEVER EXECUTED.
  // main_header.js:197,204-205,208,243 — the AHU list the whole header derives
  // from (ahus, ahus_grouped, the default main-AHU pick, and canGenerate's
  // length test) is the FILTERED list. Only 'fmi_assembly' filters anything,
  // and it cannot be the current report on dev3 (no `ksdm_master_ahu_report`
  // row), so this is identity for every reachable report value — the memo is
  // what keeps that identity stable for the memos/effects below.
  const ahus = useMemo(
    () => filterAhusForCurrentReport(ahusAll, currentReportValue),
    [ahusAll, currentReportValue]
  );
  const reportTag = currentReportValue ?? 'empty_board';

  // Legacy quirk parity (action.js:26-35): #empty_board starts with .active
  // (main.tmpl:23) and the EmptyBoard view is only constructed once an
  // open:report dispatch has removed that class — i.e. once the hash has EVER
  // carried a report tag other than 'empty_board' (ANY tag, even one not in
  // this role's report list — openReport runs for unknown tags too). Until
  // then the live board shows a BLANK pane (verified on dev3 2026-07-11):
  // selecting only a project does not populate it.
  const [boardEverOpened, setBoardEverOpened] = useState<boolean>(() => {
    const t = parseHash(window.location.hash).report;
    return !!t && t !== 'empty_board';
  });
  useEffect(() => {
    if (hashState.report && hashState.report !== 'empty_board') {
      setBoardEverOpened(true);
    }
  }, [hashState.report]);

  // Preloader (main.tmpl:15) stays active until the header's boot projects
  // fetch resolves (main_header.js render :248 removes it).
  const [headerBooted, setHeaderBooted] = useState(false);
  const onHeaderBooted = useCallback(() => setHeaderBooted(true), []);

  // Left-menu resizer (main.tmpl:7-8; CSS-driven collapse + footer reflow).
  const [menuCollapsed, setMenuCollapsed] = useState(false);

  // ---- 5b generation UI state ----
  // Report whose in-progress pane is showing; while set, #empty_board loses
  // .active (action.js:219,223 / :176-180).
  const [generating, setGenerating] = useState<IReportType | null>(null);
  // 4.5s 'opacity-0' fade on the in-progress pane (action.js:124-128).
  const [generationFaded, setGenerationFaded] = useState(false);
  // 5c: generate POST resolved — the legacy pane is EMPTY until the tab
  // view's $.ajax success runs render() (e.g. weight_calc.js:114-116); only
  // the ported per-tab panes (GENERATED_TAB_PANES) consume this.
  const [paneRendered, setPaneRendered] = useState(false);
  // .report-tabs-preloader active — set by the empty-board ⟳ (empty_board.js:38),
  // cleared by the pane render / the 4.5s timer / the guard.
  const [tabsPreloader, setTabsPreloader] = useState(false);
  // Submittal 1h guard popup (action.js:213-218 + information_popup.tmpl).
  const [guardPopupOpen, setGuardPopupOpen] = useState(false);
  // selected_project_name() equivalent, lifted from the header's caption
  // (main_header.js:1004-1016 falls back to the button text, i.e. 'All').
  const [projectName, setProjectName] = useState('All');
  // Dev-sync I — the Coil Analytics reporting period. Defaults: Jan 1 of the
  // current year → today; initialized ONCE and kept for the page's lifetime so
  // edits survive re-renders/report switches (the legacy header view's
  // coilAnalyticsStartDate/EndDate, main_header.js:123-134 on merged dev).
  const [coilAnalyticsRange, setCoilAnalyticsRange] = useState(() => {
    const today = new Date();
    return {
      start: coilAnalyticsIsoDate(new Date(today.getFullYear(), 0, 1)),
      end: coilAnalyticsIsoDate(today),
    };
  });
  const onCoilAnalyticsDateChange = useCallback((field: 'start' | 'end', iso: string) => {
    setCoilAnalyticsRange((prev) => ({ ...prev, [field]: iso }));
  }, []);
  // 5d: the dispatch states of the current generate run (one per
  // 'generate:report:<name>' event) — the data panes fetch per state like
  // the legacy views do per dispatch.
  const [generateStates, setGenerateStates] = useState<GenerateState[]>([]);
  // 5d: re-open of a cached generated report. Legacy chain: open:report /
  // menu click → header re-render → open:empty_board → reportIsGenerated
  // (action.js:240-249) → the tab's LIVE open:generated_report handler
  // re-renders from its this.data cache and generated=true removes the
  // empty board (action.js:60-64). Evaluated at navigation moments only —
  // NOT on cache writes — so a result arriving after the 5s revert stays
  // behind the empty board until the user re-opens the report, like legacy.
  const [reopenKey, setReopenKey] = useState<string | null>(null);
  // 5f (verifier fix): whether the 4.5s 'opacity-0' fade LANDED on a rendered
  // shell, per cache key. Legacy timer2 (action.js:124-128) addClass()es
  // `#<report> .generation-in-progress` at t+4.5s — if the tab's POST-success
  // render() came LATER, the selector matched nothing and the persistent pane
  // keeps an UNFADED shell, which is what a reopen re-shows (live-verified on
  // the legacy board: submittal 591 POST resolved ~t+8.6s → reopen gip WITHOUT
  // opacity-0). Tracked at onSettling via a ref (timer closures see stale
  // state) and read by the ReopenedPane's `faded` prop below.
  const reopenFadeRef = useRef<Map<string, boolean>>(new Map());
  const paneRenderedRef = useRef(false);

  // ---- AHU derivations ----
  const ahusGrouped = useMemo(() => groupAhusByMain(ahus), [ahus]);
  const mainAhus = useMemo(() => ahus.filter((a) => !a.parent_version_id), [ahus]);
  // Single-selected AHU row (deep link / auto-select) → menu report gating
  // (main_header.js:182 → menu.js activateAHUReports).
  const selectedAhu =
    Number(ahuId ?? 0) > 0 ? ahus.find((a) => String(a.id) === ahuId) ?? null : null;

  // selected_ahu_ids render default: EVERY main AHU id (main_header.js:177-178
  // — disabled section rows included until a checkbox interaction re-syncs);
  // [] while no project (branch B :278). Reset on every legacy re-render
  // trigger (report/project/AHU-list/single-AHU change).
  const [selectedAhuIds, setSelectedAhuIds] = useState<number[]>([]);
  useEffect(() => {
    if (projectId != null) setSelectedAhuIds(mainAhus.map((a) => a.id));
    else setSelectedAhuIds([]);
  }, [mainAhus, projectId, currentReportValue, ahuId]);

  // Auto-select the single main AHU (main_header.js:179-181 → selectAhu hash
  // navigate :494-497). Settles once the hash carries the ahu id.
  useEffect(() => {
    if (projectId == null || ahusData == null) return;
    if (mainAhus.length === 1 && !Number(ahuId ?? 0)) {
      navigateHash(`${reportTag}*${projectId}*${mainAhus[0].id}`);
    }
  }, [ahusData, mainAhus, projectId, ahuId, reportTag]);

  // ---- Gating ----
  // Header Generate button: the render-time truth table
  // (main_header.js:184-222 / :299-326). Deliberately does NOT depend on the
  // multi-select — the legacy button only updates on re-render.
  const canGenerate = computeCanGenerate(currentReport, projectId, ahuId, ahus.length);

  // Empty board: live value — AHU-scoped reports re-derive from the
  // multi-select picks (_triggerEmptyBoard :640-649).
  const hasProject = !!projectId && projectId !== '0';
  const boardCanGenerate = isAhuReport(currentReport)
    ? hasProject && selectedAhuIds.length > 0
    : canGenerate;
  const emptyBoardMessage = computeEmptyBoardMessage(
    currentReport,
    boardCanGenerate,
    projectId,
    selectedAhuIds
  );

  const ui = computeConditionalUI(currentReport, projectId);

  // ---- Legacy side effects ----
  // body[data-user_id] (reportBase.html:12).
  useEffect(() => {
    const id = getBootUserId();
    if (id != null) document.body.setAttribute('data-user_id', String(id));
    return () => {
      document.body.removeAttribute('data-user_id');
    };
  }, []);

  // Height chain: report skeleton.css:174 sizes #ksdm-content off the
  // html/body 100% chain; #react-root must not break it (same repair as the
  // AHU board).
  useEffect(() => {
    const root = document.getElementById('react-root');
    const prev = root?.style.height ?? '';
    if (root) root.style.height = '100%';
    return () => {
      if (root) root.style.height = prev;
    };
  }, []);

  // sessionStorage 'genereate-report-data' — written on every render pass
  // (main_header.js:231-237); 5b's generation flow reads it back. [sic] the
  // legacy key misspelling.
  useEffect(() => {
    sessionStorage.setItem(
      'genereate-report-data',
      JSON.stringify({
        canGenerate,
        data: {
          project_id: projectId,
          ahu_id: ahuId,
          selected_ahu_ids: selectedAhuIds,
          current_report: currentReportValue ?? undefined,
          reportType: getReportType(currentReport),
        },
      })
    );
  }, [canGenerate, projectId, ahuId, selectedAhuIds, currentReportValue, currentReport]);

  // ---- Print resolution (5f) — full trigger inventory of the legacy mvc
  // tree; every print path is DEAD except one:
  //  - #print header button ('click #print' → print, main_header.js:63):
  //    DEAD — main_header.tmpl renders only #generate_report (:45), no
  //    #print/#export_csv/#export_pdf buttons.
  //  - 'print:report' → printReport → 'print:report:<current>' relay
  //    (action.js:24, :236-238): DEAD — nothing anywhere triggers the
  //    unsuffixed 'print:report'.
  //  - header export_pdf (main_header.js:968-1008, fires
  //    'print:report:<name>' for 5 reports at :982-998): DEAD — its only
  //    trigger is 'click #export_pdf' (:64), button not rendered.
  //  - evap_pack_data's 'print:report:evap_pack_data' → export_pdf listener
  //    (evap_pack_data.js:27): DEAD — that event is triggered nowhere (not
  //    even inside print(), which routes evap_pack_data through the generic
  //    DOM-copy branch).
  //  - style#print host (main.tmpl:6, mirrored below): an EMPTY media=print
  //    style tag — no JS ever writes into it; print styling is the compiled
  //    css/sass/print/_*.scss @media-print blocks (static CSS). report.html/
  //    reportBase.html add no print trigger or CSS of their own.
  //  - LIVE: Ctrl+P — main_header.js:128-132 binds $(document) keyup+keydown
  //    (no preventDefault, so it double-fires AND the native dialog still
  //    opens) once the initialize projects fetch resolves (:121-134) →
  //    print() (:686-966): reads the open report's value off the
  //    .current_report menu button (:706-710; buttons carry
  //    value="{{value}}", menu.tmpl:15,30), builds the body-level
  //    .report-print-view table from the active pane's DOM, dispatches
  //    'print:report:<name>' for 10 values (:832-876 — listeners exist only
  //    for phx_selection.js:36 → print :297-326, submittal.package.js:45
  //    ['submittal_fan_data'] → print :103, coil_data.js:27 → export_pdf,
  //    which throws on its never-written this.data cache; the other 7 fire
  //    into a void), then window.print() + cleanup for every report NOT in
  //    the :951-960 skip list. So the ~280-line print builder IS still
  //    reachable in legacy, via Ctrl+P only. NOT ported here (the 5c/5e
  //    deferral stands): port-or-drop is an orchestrator decision, recorded
  //    outside this file; the React board's Ctrl+P is the browser-native
  //    print of the visible page.

  // ---- 5d: reopen evaluation (reportIsGenerated, action.js:240-249) ----
  // The key uses the CURRENT header state with '0' defaults (:242-243); a
  // cache hit for a reopenable tab shows the cached pane instead of the
  // empty board. 5e: reportCacheKey — the order tabs key by report+project
  // only (their legacy `data.current_report + data.project_id`).
  const evalReopenKey = useCallback(
    (reportValue: string | null): string | null => {
      if (!reportValue || !REOPENABLE_TABS.has(reportValue)) return null;
      const key = reportCacheKey({
        current_report: reportValue,
        project_id: projectId && projectId !== '0' ? projectId : '0',
        ahu_id: ahuId && ahuId !== '0' ? ahuId : '0',
      });
      return hasGeneratedReport(key) ? key : null;
    },
    [projectId, ahuId]
  );

  // Every open:report moment re-checks the cache: hash navigation (router →
  // openReport → header render → open:empty_board) and the equivalent header
  // re-render triggers.
  useEffect(() => {
    setReopenKey(evalReopenKey(currentReportValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashState, currentReportValue, evalReopenKey]);

  // 5e: EVERY hash navigation runs openReport, which deactivates every report
  // pane ($('#report_tabs div').removeClass('active'), action.js:83) — report
  // switches AND project/AHU re-selections alike. This matters most for
  // service_order/shipping_order, whose generate skips the 5s revert entirely
  // (action.js:115-118): without this, their pane (and the suppressed empty
  // board) would outlive navigation. The dispatch's socket leg keeps running
  // in the kit; the cached result comes back through the reopen evaluation
  // above. (Deliberate simplification: legacy re-adds .active to the SAME
  // report's stale pane and stacks the re-activated empty board on top of it
  // — here the empty board simply returns alone.) A generate never navigates,
  // so an active run is only cleared by actual user navigation.
  useEffect(() => {
    if (generating) setGenerating(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashState]);

  // ---- Navigation (all go through the hash, like Backbone.history) ----
  const onSelectReport = (value: string) => {
    // clickMenuItem (menu.js:96-105): value + current project + current ahu.
    const p = Number(projectId ?? 0) ? `*${projectId}` : '';
    const a = Number(ahuId ?? 0) ? `*${ahuId}` : '';
    navigateHash(`${value}${p}${a}`);
    // menu.js triggers 'open:report' directly (no hash round-trip), so
    // re-clicking the SAME report also re-runs reportIsGenerated — evaluate
    // here for the no-hashchange case.
    setReopenKey(evalReopenKey(value !== 'empty_board' ? value : null));
  };

  const onSelectProject = (id: string | null) => {
    // selectProject (main_header.js:457-480): drops the AHU segment.
    navigateHash(`${reportTag}${id ? `*${id}` : ''}`);
  };

  // ---- 5b: Generate (generate_report_icon → generate:report chain) ----
  // Both entry points funnel here like the legacy 'generate_report_icon:
  // clicked' dispatch into main_header.generateReport (main_header.js:74-78).
  const onGenerate = useCallback(
    (fromEmptyBoard: boolean) => {
      if (!currentReport) return;
      if (fromEmptyBoard) {
        // empty_board.js:36-41 — the icon path arms the tabs preloader and
        // the 'is-generate' session flag (read by the socket-disconnect
        // handler in universal_map.js:56-77).
        setTabsPreloader(true);
        sessionStorage.setItem('is-generate', 'true');
      }
      // The legacy re-snapshots the multi-select from the DOM EXCLUDING
      // disabled rows (main_header.js:516-519) — the render default in
      // selectedAhuIds still contains disabled section rows, so filter here.
      const flagKey = sectionFlagKey(currentReport.value);
      const picked = selectedAhuIds.filter((id) => {
        if (!flagKey) return true;
        const row = ahus.find((a) => a.id === id);
        return row ? !!row.reports?.[flagKey] : true;
      });
      // 5d: snapshot the run's dispatch states for the data panes (the
      // dispatches run synchronously, one onDispatched per state).
      const runStates: GenerateState[] = [];
      const genUi: GenerateUi = {
        onBlocked: () => {
          setTabsPreloader(false); // action.js:215
          setGuardPopupOpen(true); // :216 → main.js:57-59
        },
        onDispatched: (state) => {
          runStates.push(state);
          setGenerateStates([...runStates]);
          setReopenKey(null); // a new run replaces any reopened pane
          setGenerating(currentReport); // :219,223
          setGenerationFaded(false);
          // Immediate-render tabs (filter_data / er_wheel_selection /
          // ahu_model) render their shell synchronously at dispatch — their
          // legacy render() also clears the tabs preloader (e.g.
          // er_wheel_selection.js:147-149 + render :40-46). Everything else
          // stays empty until the POST succeeds.
          const immediate = IMMEDIATE_RENDER_TABS.has(state.current_report);
          setPaneRendered(immediate);
          paneRenderedRef.current = immediate;
          if (immediate) setTabsPreloader(false);
        },
        onSettling: () => {
          setGenerationFaded(true); // :125
          setTabsPreloader(false); // :126
          // 5f (verifier fix): record whether this run's fade landed on a
          // rendered shell — the bit a reopen must reproduce (see
          // reopenFadeRef). One entry per dispatch state of the run.
          runStates.forEach((s) => {
            reopenFadeRef.current.set(reportCacheKey(s), paneRenderedRef.current);
          });
        },
        onFinished: () => {
          setGenerating(null); // :176-180 — empty board re-activates
        },
        onTabRendered: (state) => {
          // 5c panes only — out-of-scope reports keep the 5b generic pane
          // untouched. Legacy render() fills the tab shell AND deactivates
          // .report-tabs-preloader on POST success (e.g. weight_calc.js:37-43).
          if (GENERATED_TAB_PANES[state.current_report]) {
            setPaneRendered(true);
            paneRenderedRef.current = true;
            setTabsPreloader(false);
          }
        },
      };
      generateReport(
        {
          report: currentReport,
          projectId,
          ahuId,
          projectName,
          pickedAhuIds: picked,
          selectedAhuIds,
          // dev-sync G / CB-2 — `self.data.ahus` (main_header.js:583-585);
          // read only to look up an FMI pick's fmi_assembly_page_count.
          ahuRows: ahus,
          // Dev-sync I — the header's Coil Analytics dates; stamped onto the
          // dispatch state only for coil_analytics (buildGenerateStates).
          coilAnalyticsRange,
        },
        genUi
      );
    },
    [currentReport, projectId, ahuId, projectName, selectedAhuIds, ahus, coilAnalyticsRange]
  );

  // 5c per-tab pane for the report being generated (undefined → 5b generic).
  const GeneratedPane = generating ? GENERATED_TAB_PANES[generating.value] : undefined;

  // 5d: reopened cached report (phx_selection / ahu_model). While showing,
  // #empty_board loses .active like the legacy generated branch
  // (action.js:60-64); the pane renders from the reportTabData cache.
  const reopenedReport =
    !generating && reopenKey && currentReport && GENERATED_TAB_PANES[currentReport.value]
      ? currentReport
      : null;
  const ReopenedPane = reopenedReport ? GENERATED_TAB_PANES[reopenedReport.value] : undefined;

  return (
    <>
      {/* Generic popup host (server shell parity). */}
      <div id="wrapper-div" className="popup popup-wrapper" />

      {/* Shared header — app name 'report' (reportBase.html:14 shell class,
          header.tmpl workflow gating excludes 'report'). */}
      <GlobalHeader appName="report" />

      <div id="ksdm-content">
        {/* main.tmpl:6-8 — print-style host + left-menu resizer. */}
        <style type="text/css" media="print" id="print" />
        <input
          id="left_resizer"
          type="checkbox"
          className="left-menu-resizer whiteboard-resizer"
          checked={menuCollapsed}
          onChange={(e) => setMenuCollapsed(e.target.checked)}
        />
        <label htmlFor="left_resizer" className="ka-has-tooltip left_resizer_label" tooltip="Expand">
          <i className="icon-hamburger" />
          <h2>Reports</h2>
          <i className="icon-angle-left ka-has-tooltip" tooltip="Collapse" />
        </label>

        <ReportsMenu
          reports={reports}
          groups={groups}
          currentReportValue={currentReportValue}
          selectedAhu={selectedAhu}
          onSelectReport={onSelectReport}
        />

        <div id="main_content" className="main-container">
          <div
            className={`preloader dotted-preloader report-preloader${
              headerBooted ? '' : ' active'
            }`}
          />
          <div className="header-buttons" id="main_header">
            <ReportsHeader
              projectId={projectId}
              ahusGrouped={ahusGrouped}
              reportValue={currentReportValue}
              canGenerate={canGenerate}
              ui={ui}
              onSelectProject={onSelectProject}
              onAhuIdsChange={setSelectedAhuIds}
              onProjectNameChange={setProjectName}
              onGenerate={() => onGenerate(false)}
              ahuResetKey={`${currentReportValue ?? ''}|${projectId ?? ''}|${ahuId ?? ''}`}
              onBooted={onHeaderBooted}
              coilAnalyticsRange={coilAnalyticsRange}
              onCoilAnalyticsDateChange={onCoilAnalyticsDateChange}
            />
          </div>
          <div className="tabs row" id="report_tabs">
            <div
              className={`preloader dotted-preloader report-tabs-preloader${
                tabsPreloader ? ' active' : ''
              }`}
            />
            {/* 5b: while generating, the report pane shows and #empty_board
                loses .active (action.js:219,223), reverting at 5s (:176-180).
                5c: the generate-only reports mount their ported per-tab panes
                (GENERATED_TAB_PANES); the remaining tabs are 5d/5e. */}
            <div id="empty_board" className={generating || reopenedReport ? '' : 'active'}>
              {boardEverOpened && (
                <EmptyBoard
                  message={emptyBoardMessage}
                  submittalHint={currentReportValue === 'submittal_package'}
                  onGenerate={() => onGenerate(true)}
                />
              )}
            </div>
            {generating &&
              (GeneratedPane ? (
                <GeneratedPane
                  report={generating}
                  faded={generationFaded}
                  rendered={paneRendered}
                  states={generateStates}
                  mode="generate"
                />
              ) : (
                <GeneratingReportTab report={generating} faded={generationFaded} />
              ))}
            {ReopenedPane && reopenedReport && (
              <ReopenedPane
                report={reopenedReport}
                // 5f (verifier fix): the stale shell is faded ONLY when the
                // generate-time 4.5s fade landed on a rendered shell (legacy
                // timer2 semantics — see reopenFadeRef). Default true keeps
                // the fast-POST behavior for keys with no record.
                faded={reopenKey ? reopenFadeRef.current.get(reopenKey) ?? true : true}
                rendered
                mode="reopen"
                reopenKey={reopenKey}
              />
            )}
          </div>
          {/* Submittal 1h guard popup — appended to #main_content like the
              legacy 'show:report:generation:popup' handler (main.js:57-59). */}
          {guardPopupOpen && <ReportGenerationPopup onClose={() => setGuardPopupOpen(false)} />}
        </div>
      </div>

      <ReportsBoardFooter menuCollapsed={menuCollapsed} />
    </>
  );
};

export default ReportsBoardPage;
